import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { shareProperty } from '../utils/share';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Bed, Bath, Maximize, MapPin, TrendingUp, BarChart3, Calculator, ArrowLeft, MessageCircle, Share2 } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para obtener la URL correcta de una imagen
const getImageUrl = (img) => {
  if (!img) return null;
  let url = typeof img === 'object' ? img.url : img;
  if (url && url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper para obtener array de URLs de imágenes
const getImageUrls = (images) => {
  if (!images || images.length === 0) return [];
  return images.map(img => getImageUrl(img)).filter(url => url);
};

export const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, dataVersion } = useAuth();
  const { theme, themes } = useTheme();
  const currentTheme = themes[theme] || themes.electric;
  const [property, setProperty] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    loadProperty();
  }, [id, dataVersion]); // Recargar al cambiar dataVersion

  const loadProperty = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/properties/${id}?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setProperty(response.data);
      
      if (['profesional', 'premium'].includes(user?.subscription_plan)) {
        try {
          const analyticsResponse = await axios.get(`${API}/analytics/${id}?t=${timestamp}`, { 
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          setAnalytics(analyticsResponse.data);
        } catch (error) {
          console.error('Analytics error:', error);
        }
      }
    } catch (error) {
      console.error('Error loading property:', error);
    } finally {
      setLoading(false);
    }
  };

  // Análisis con IA (Haiku): carga aparte para no bloquear la ficha; el backend
  // lo calcula una vez y lo cachea, así que sucesivas visitas son instantáneas.
  useEffect(() => {
    if (property && ['profesional', 'premium'].includes(user?.subscription_plan)) {
      loadAiStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.property_id, user?.subscription_plan]);

  const loadAiStats = async () => {
    setAiLoading(true);
    try {
      const res = await axios.get(`${API}/properties/${id}/ai-stats`);
      setAiStats(res.data);
    } catch (error) {
      setAiStats(null);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !property) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-xl font-bold text-white">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8 lg:p-12">
      <div className="max-w-6xl mx-auto pt-12">
        {/* Back and Share buttons */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Volver al mapa</span>
          </button>

          <button
            onClick={() => {
              if (!property) return;
              
              const shareData = {
                title: property.title,
                text: `${property.title} - €${property.price.toLocaleString('es-ES')} en ${property.location}`,
                url: `${window.location.origin}/property/${property.property_id}`
              };
              
              // Intentar share nativo (móvil)
              if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                navigator.share(shareData)
                  .then(() => toast.success('Propiedad compartida'))
                  .catch((error) => {
                    if (error.name !== 'AbortError') {
                      copyToClipboard(shareData.url);
                    }
                  });
              } else {
                // Fallback para PC: copiar al portapapeles
                copyToClipboard(shareData.url);
              }
              
              function copyToClipboard(text) {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(text)
                    .then(() => toast.success('Enlace copiado al portapapeles ✓'))
                    .catch(() => {
                      // Fallback antiguo (para navegadores viejos)
                      const textarea = document.createElement('textarea');
                      textarea.value = text;
                      textarea.style.position = 'fixed';
                      textarea.style.opacity = '0';
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                      toast.success('Enlace copiado al portapapeles ✓');
                    });
                } else {
                  toast.error('Tu navegador no soporta copiar al portapapeles');
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20 font-medium transition-all"
          >
            <Share2 size={18} />
            <span className="text-sm">Compartir</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${
              property.listing_type === 'alquiler' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
            }`}>
              {property.listing_type === 'alquiler' ? 'ALQUILER' : 'VENTA'}
            </span>
            <span className="text-xs text-gray-500 uppercase">{property.property_type}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="property-title">
            {property.title}
          </h1>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <MapPin size={14} />
            <span>{property.location}</span>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-5 gap-6 mb-6">
          {/* Gallery */}
          <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-white/10" data-testid="property-gallery">
            {property.images && property.images.length > 0 ? (
              <Carousel
                showArrows={true}
                showThumbs={property.images.length > 1}
                infiniteLoop={true}
                autoPlay={true}
                interval={5000}
                showStatus={false}
                className="property-carousel cursor-pointer"
                onClickItem={(index) => {
                  setLightboxIndex(index);
                  setLightboxOpen(true);
                }}
              >
                {getImageUrls(property.images).map((imgUrl, idx) => (
                  <div key={idx} className="h-[350px] md:h-[450px]">
                    <img 
                      src={imgUrl} 
                      alt={`${property.title} - ${idx + 1}`} 
                      className="h-full w-full object-cover" 
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))}
              </Carousel>
            ) : (
              <div className="h-[350px] bg-gray-800 flex items-center justify-center text-gray-500">
                Sin imágenes
              </div>
            )}
          </div>
          
          {/* Info Card */}
          <div className="lg:col-span-2 bg-gray-900 border border-white/10 rounded-2xl p-6 flex flex-col">
            <div className={`text-4xl font-bold bg-gradient-to-r ${currentTheme.gradient} bg-clip-text text-transparent mb-1`} data-testid="property-price">
              {property.price.toLocaleString('es-ES')}
              {property.listing_type === 'alquiler' && <span className="text-lg text-gray-400 ml-1">/mes</span>}
            </div>
            
            <div className="grid grid-cols-3 gap-3 my-6">
              <div className="bg-gray-800 rounded-xl p-4 text-center">
                <Bed size={20} className="mx-auto text-gray-400 mb-1" />
                <div className="text-xl font-bold text-white" data-testid="property-bedrooms">{property.bedrooms}</div>
                <div className="text-[10px] text-gray-500 uppercase">Habitaciones</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 text-center">
                <Bath size={20} className="mx-auto text-gray-400 mb-1" />
                <div className="text-xl font-bold text-white" data-testid="property-bathrooms">{property.bathrooms}</div>
                <div className="text-[10px] text-gray-500 uppercase">Banos</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 text-center">
                <Maximize size={20} className="mx-auto text-gray-400 mb-1" />
                <div className="text-xl font-bold text-white" data-testid="property-area">{property.area}</div>
                <div className="text-[10px] text-gray-500 uppercase">m2</div>
              </div>
            </div>
            
            <div className="space-y-3 flex-1">
              <div className="flex justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-gray-400">Tipo</span>
                <span className="text-white capitalize" data-testid="property-type">{property.property_type}</span>
              </div>
              <div className="flex justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-gray-400">Operacion</span>
                <span className="text-white capitalize" data-testid="property-listing-type">{property.listing_type}</span>
              </div>
              {property.city && (
                <div className="flex justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-gray-400">Ciudad</span>
                  <span className="text-white">{property.city}</span>
                </div>
              )}
            </div>
            
            <button
              onClick={() => navigate(`/messages?property=${id}&owner=${property.user_id}`)}
              data-testid="contact-btn"
              className={`w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r ${currentTheme.gradient} text-white font-medium py-3 rounded-xl hover:opacity-90 transition-all`}
            >
              <MessageCircle size={18} />
              Contactar propietario
            </button>
          </div>
        </div>
        
        {/* Description */}
        <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Descripcion</h2>
          <p className="text-gray-400 leading-relaxed text-sm whitespace-pre-wrap" data-testid="property-description">{property.description}</p>
        </div>
        
        {/* Premium Analytics */}
        {analytics && ['profesional', 'premium'].includes(user?.subscription_plan) && (
          <div className={`bg-gradient-to-r ${currentTheme.gradient} rounded-2xl p-6 mb-6`} data-testid="premium-analytics">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp size={20} />
              Análisis Profesional
            </h2>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-xs font-medium text-white/70 uppercase mb-1">ROI Anual</div>
                <div className="text-2xl font-bold text-white" data-testid="analytics-roi">{analytics.roi != null ? `${analytics.roi}%` : '—'}</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-xs font-medium text-white/70 uppercase mb-1">Precio Mercado</div>
                <div className="text-lg font-bold text-white" data-testid="analytics-market">
                  {analytics.market_price_status === 'within_market' ? 'En mercado' :
                   analytics.market_price_status === 'below_market' ? 'Por debajo' : 'Por encima'}
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-xs font-medium text-white/70 uppercase mb-1">Revalorización 5 años</div>
                <div className="text-2xl font-bold text-white" data-testid="analytics-appreciation">+{analytics.appreciation_5y}%</div>
              </div>
            </div>
          </div>
        )}

        {/* Análisis con IA (Haiku) — complementa los números financieros de arriba */}
        {['profesional', 'premium'].includes(user?.subscription_plan) && (aiLoading || aiStats) && (
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 mb-6" data-testid="ai-stats">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <BarChart3 size={20} className="text-cyan-400" />
              Análisis con IA
              <span className="text-[10px] font-medium text-white/40 uppercase tracking-wide">Haiku</span>
            </h2>
            {aiLoading && !aiStats ? (
              <p className="text-gray-400 text-sm">Analizando con IA…</p>
            ) : aiStats ? (
              <>
                <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">
                  {(aiStats.summary || '').replace(/\*\*/g, '')}
                </p>
                {aiStats.data && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="bg-white/10 rounded-lg px-3 py-1 text-xs text-white/80">
                      {aiStats.data.vistas ?? 0} vistas
                    </span>
                    <span className="bg-white/10 rounded-lg px-3 py-1 text-xs text-white/80">
                      {aiStats.data.dias_publicado ?? '—'} días publicado
                    </span>
                    <span className="bg-white/10 rounded-lg px-3 py-1 text-xs text-white/80">
                      {aiStats.data.comparables?.count > 0
                        ? `${aiStats.data.comparables.count} comparables de zona`
                        : 'Sin comparables de zona'}
                    </span>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {!analytics && !['profesional', 'premium'].includes(user?.subscription_plan) && (
          <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <BarChart3 size={18} className="text-yellow-400" />
              Desbloquea Análisis Profesional
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Con el plan Profesional obtienes ROI, analisis de mercado, proyeccion de revalorizacion y calculo de plusvalia para cada propiedad.
            </p>
            <button
              onClick={() => navigate('/select-plan')}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-medium py-2.5 px-5 rounded-lg text-sm hover:opacity-90 transition-all"
            >
              Ver Planes
            </button>
          </div>
        )}
        
        {/* Map */}
        <div className="rounded-2xl overflow-hidden border border-white/10 h-80 relative z-0">
          <MapContainer
            center={[property.coordinates.lat, property.coordinates.lng]}
            zoom={typeof window !== 'undefined' && window.innerWidth < 768 ? 13 : 14}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[property.coordinates.lat, property.coordinates.lng]} />
          </MapContainer>
        </div>
        
        {/* Lightbox Modal para ampliar imágenes */}
        {lightboxOpen && property.images && property.images.length > 0 && (
          <div 
            className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center p-4"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-[10000]"
            >
              ×
            </button>
            <div className="max-w-7xl w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <Carousel
                selectedItem={lightboxIndex}
                showArrows={true}
                showThumbs={true}
                infiniteLoop={true}
                showStatus={true}
                className="lightbox-carousel"
                onChange={(index) => setLightboxIndex(index)}
              >
                {getImageUrls(property.images).map((imgUrl, idx) => (
                  <div key={idx} className="flex items-center justify-center" style={{ height: '85vh' }}>
                    <img 
                      src={imgUrl} 
                      alt={`${property.title} - ${idx + 1}`} 
                      className="max-h-full max-w-full object-contain" 
                    />
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
